import { createPortal } from 'react-dom';
import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { ArrowsLeftRight, Check, MagnifyingGlass } from '@phosphor-icons/react';

const nameOf = program => program.name || 'Untitled program';

export default function IncubatorProgramSwitcher({ programs, selectedId, onSelect }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const root = useRef(null), trigger = useRef(null), search = useRef(null), panelRef = useRef(null);
  const [panelStyle, setPanelStyle] = useState({});
  const [themeVars, setThemeVars] = useState({});
  const id = useId();
  const selected = programs.find(program => program.id === selectedId);
  const matches = programs.filter(program => nameOf(program).toLowerCase().includes(query.trim().toLowerCase()));
  const activeIndex = Math.min(highlighted, Math.max(0, matches.length - 1));
  const close = (restoreFocus = false) => { setOpen(false); if (restoreFocus) trigger.current?.focus(); };
  const choose = program => { onSelect(program.id); close(true); };

  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const anchor = trigger.current?.getBoundingClientRect();
      if (!anchor) return;
      const viewport = window.visualViewport;
      const leftEdge = viewport?.offsetLeft || 0;
      const topEdge = viewport?.offsetTop || 0;
      const vw = viewport?.width || window.innerWidth;
      const vh = viewport?.height || window.innerHeight;
      const margin = 12;
      const width = Math.min(320, Math.max(180, vw - margin * 2));
      const left = Math.max(leftEdge + margin, Math.min(anchor.right - width, leftEdge + vw - width - margin));
      const below = topEdge + vh - anchor.bottom - margin - 8;
      const above = anchor.top - topEdge - margin - 8;
      const upwards = below < 270 && above > below;
      const available = Math.max(100, upwards ? above : below);
      setPanelStyle({position:'fixed', left, right:'auto', top:upwards?'auto':anchor.bottom + 8,
        bottom:upwards?topEdge + vh - anchor.top + 8:'auto',
        width, maxHeight:available, zIndex:10000, '--inc-program-menu-height':`${available}px`});
    };
    // A portal is outside .inc-shell: copy only its theme tokens, never its layout/background.
    const context = trigger.current?.closest('.inc-shell') || trigger.current;
    if (context) {
      const computed = window.getComputedStyle(context);
      const vars = {};
      for (const key of ['--inc-panel', '--inc-subtle', '--inc-hover', '--inc-text', '--inc-muted', '--inc-secondary', '--inc-gold', '--inc-gold-soft', '--inc-gold-line', '--inc-line']) {
        const value = computed.getPropertyValue(key).trim();
        if (value) vars[key] = value;
      }
      setThemeVars(vars);
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    window.visualViewport?.addEventListener('resize', place);
    window.visualViewport?.addEventListener('scroll', place);
    return () => {window.removeEventListener('resize', place);window.removeEventListener('scroll', place, true);
      window.visualViewport?.removeEventListener('resize', place);
      window.visualViewport?.removeEventListener('scroll', place);};
  }, [open]);

  useEffect(() => {
    if (!open) return;
    search.current?.focus({ preventScroll: true });
    const dismiss = event => { if (!root.current?.contains(event.target) && !panelRef.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('focusin', dismiss);
    return () => { document.removeEventListener('pointerdown', dismiss); document.removeEventListener('focusin', dismiss); };
  }, [open]);
  useEffect(() => {
    if (open) document.getElementById(`${id}-option-${activeIndex}`)?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex, id]);

  const onKeys = event => {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(true); return; }
    if (!matches.length) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((activeIndex + (event.key === 'ArrowDown' ? 1 : -1) + matches.length) % matches.length);
    } else if (event.key === 'Enter') { event.preventDefault(); choose(matches[activeIndex]); }
  };
  if (!selected) return null;
  return <div ref={root} className={`inc-program-switcher ${open ? 'is-open' : ''}`}>
    <button ref={trigger} type="button" className="inc-program-switch-trigger" aria-label={`Switch program, ${nameOf(selected)}`} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? `${id}-panel` : undefined} title={nameOf(selected)} onClick={() => {
      if (open) { close(); return; }
      setQuery(''); setHighlighted(Math.max(0, programs.findIndex(program => program.id === selectedId))); setOpen(true);
    }} onKeyDown={event => { if (!open && event.key === 'ArrowDown') { event.preventDefault(); setQuery(''); setHighlighted(0); setOpen(true); } }}>
      <span className="inc-program-switch-indicator" aria-hidden="true"><i/><i/><i/></span><span key={selectedId} className="inc-program-switch-name">{nameOf(selected)}</span><ArrowsLeftRight size={14} aria-hidden="true"/>
    </button>
    {open && createPortal(<div ref={panelRef} style={{...themeVars,...panelStyle,pointerEvents:"auto"}} className="inc-program-switch-panel inc-program-switch-floating" id={`${id}-panel`} role="dialog" aria-label="Switch program" onKeyDown={onKeys}>
      <div className="inc-program-switch-heading"><span>Switch program</span><small>{programs.length} open</small></div>
      <div className="inc-program-switch-search"><MagnifyingGlass size={14} aria-hidden="true"/><input ref={search} role="combobox" aria-label="Find a program" aria-expanded="true" aria-autocomplete="list" aria-controls={`${id}-options`} aria-activedescendant={matches.length ? `${id}-option-${activeIndex}` : undefined} placeholder="Find a program…" value={query} onChange={event => { setQuery(event.target.value); setHighlighted(0); }}/></div>
      <div className="inc-program-switch-options" id={`${id}-options`} role="listbox" aria-label="Open programs">{matches.map((program, index) => <button type="button" role="option" tabIndex={-1} aria-selected={program.id === selectedId} id={`${id}-option-${index}`} key={program.id} className={index === activeIndex ? 'is-highlighted' : ''} onPointerMove={() => setHighlighted(index)} onMouseDown={event => event.preventDefault()} onClick={() => choose(program)}>
        <span className="inc-program-switch-monogram" aria-hidden="true">{nameOf(program).slice(0, 2).toUpperCase()}</span><span className="inc-program-switch-option-name">{nameOf(program)}<small>{Number(program.application_count || 0).toLocaleString('en-IN')} applications</small></span>{program.id === selectedId && <Check size={14} weight="bold" aria-hidden="true"/>}
      </button>)}</div>
      {!matches.length && <p className="inc-program-switch-empty">No matching programs.</p>}
      <div className="inc-program-switch-hint" aria-hidden="true">↑ ↓ to browse <span>↵ to switch</span></div>
    </div>, document.body)}
  </div>;
}
